import { useState } from "react";

import { InviteCreateResp } from "../../lib/api";

/** Shown after an invite is created. Behaviour depends on the channel:
 *
 *   Email invite — surfaces whether the email went out (`email_sent`),
 *   and always shows a copy-able accept link (the backend returns the
 *   token to the inviter so onboarding never depends on email working).
 *
 *   Phone invite — there's no link: the invitee joins by signing in
 *   with their number (one-time code). We just confirm who was invited
 *   and how they'll get in.
 */
export function InviteResult({ resp }: { resp: InviteCreateResp }) {
  const isPhone = !!resp.invitation.phone && !resp.invitation.email;
  const link = resp.accept_token
    ? `${window.location.origin}/invite/${resp.accept_token}`
    : null;

  if (isPhone) {
    return (
      <div className="mt-2 space-y-2">
        <div className="text-xs text-green-700 bg-green-50 rounded p-2">
          Invited{" "}
          <span className="font-medium">{resp.invitation.phone}</span>. They
          can sign in now with their phone number — choose “Phone” on the
          sign-in screen and enter the one-time code.
        </div>
      </div>
    );
  }

  return (
    <div className="mt-2 space-y-2">
      {resp.email_sent ? (
        <div className="text-xs text-green-700 bg-green-50 rounded p-2">
          Invitation emailed to{" "}
          <span className="font-medium">{resp.invitation.email}</span>.
        </div>
      ) : (
        <div className="text-xs text-amber-800 bg-amber-50 rounded p-2">
          Created, but the email couldn't be sent. Share the link below
          directly to get them onboarded.
        </div>
      )}
      {link && <CopyLink link={link} />}
    </div>
  );
}

function CopyLink({ link }: { link: string }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API blocked (rare on https) — fall back to
      // selecting the text so the user can Ctrl/Cmd-C manually.
      const el = document.getElementById(
        `invite-link-${btoa(link).slice(0, 8)}`,
      ) as HTMLInputElement | null;
      el?.select();
    }
  };

  return (
    <div className="flex items-stretch gap-2">
      <input
        id={`invite-link-${btoa(link).slice(0, 8)}`}
        readOnly
        value={link}
        onFocus={(e) => e.currentTarget.select()}
        className="flex-1 min-w-0 border rounded px-2 py-1 text-xs font-mono bg-gray-50"
      />
      <button
        type="button"
        onClick={copy}
        className={`shrink-0 text-xs font-medium px-3 py-1 rounded ${
          copied
            ? "bg-green-600 text-white"
            : "bg-agrilens-green text-white hover:bg-agrilens-green-dark"
        }`}
      >
        {copied ? "Copied!" : "Copy link"}
      </button>
    </div>
  );
}
